
import { MsalProvider, AuthenticatedTemplate, UnauthenticatedTemplate, useMsal } from "@azure/msal-react";
import { ApolloProvider, ApolloClient, ApolloLink, createHttpLink, InMemoryCache, useQuery } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { apiTokenRequest, loginRequest } from "./AuthConfig";
import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import HobtalkLoader from './components/common/HobtalkLoader';
import Navbar from './components/layout/Navbar';
import LandingView from './views/landing/LandingView';
import HomeDashboard from './views/home/HomeDashboard';
import HobbyDetailView from './views/hobbies/HobbyDetailView';
import InsightsView from './views/insights/InsightsView';
import AccountView from './views/account/AccountView';
import ShareHighlightHost from './components/shareHighlights/ShareHighlightHost';
import { GET_MY_PROFILE } from './views/account/operations';
import { commonStrings } from './content';
import {
  apolloCacheKeyForAccount,
  clearPersistedApolloCache,
  restoreApolloCache,
  scheduleApolloCachePersist,
} from './services/apolloCachePersistence';
import { configureTelemetry, trackClientError, trackTelemetry } from './services/telemetry';
import { invalidateHomeQueries, shouldInvalidateHome } from './services/homeCacheInvalidation';
import './App.css';

const httpLink = createHttpLink({
  // Uses the build-time variable if it exists, otherwise falls back to local
  uri: process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:7071/api/graphql',
});

/**
 * Updates paginated results.
 *
 * @param {*} existing - existing value.
 * @param {*} incoming - incoming value.
 * @param {object} options3 - options3 value.
 * @param {*} options3.args - args value.
 * @returns {*} The derived operation result.
 */
function mergePaginatedResults(existing, incoming, { args }) {
  if (!args?.after) {
    return incoming;
  }
  const existingNodes = existing?.nodes || [];
  const incomingNodes = incoming?.nodes || [];
  const seen = new Set(existingNodes.map((node) => node?.__ref || node?.id).filter(Boolean));
  return {
    ...incoming,
    nodes: [
      ...existingNodes,
      ...incomingNodes.filter((node) => {
        const key = node?.__ref || node?.id;
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }),
    ],
  };
}

/**
 * Creates apollo cache.
 *
 * @returns {*} The derived operation result.
 */
function createApolloCache() {
  const paginatedField = {
    keyArgs: false,
    merge: mergePaginatedResults,
  };

  return new InMemoryCache({
    typePolicies: {
      Query: {
        fields: {
          getHomeFeedPage: paginatedField,
          getRecipesListPage: paginatedField,
          getMoviesPage: paginatedField,
          getTVShowsListPage: paginatedField,
          getBooksListPage: paginatedField,
          getTripsListPage: paginatedField,
          getFitnessWorkoutsListPage: paginatedField,
          getHikesListPage: paginatedField,
        },
      },
    },
  });
}

/**
 * Determines whether auth failure.
 *
 * @param {*} error - Failure being normalized or reported.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function isAuthFailure(error) {
  const message = String(error?.message || '').toLowerCase();
  const code = String(error?.extensions?.code || error?.code || '').toUpperCase();
  const statusCode = error?.statusCode || error?.status || error?.response?.statusCode || error?.response?.status;
  return (
    statusCode === 401 ||
    statusCode === 403 ||
    code === 'UNAUTHENTICATED' ||
    code === 'FORBIDDEN' ||
    message.includes('user session which does not exist') ||
    message.includes('no account or login hint was passed') ||
    message.includes('interaction_required') ||
    message.includes('login_required') ||
    message.includes('consent_required')
  );
}

/**
 * Retrieves allowed identifiers.
 *
 * @returns {*} The derived operation result.
 */
function getAllowedIdentifiers() {
  return new Set(
    [
      process.env.REACT_APP_ALLOWED_USER_EMAILS,
      process.env.REACT_APP_ALLOWED_USER_IDENTIFIERS,
    ]
      .filter(Boolean)
      .join(',')
      .split(',')
      .map((identifier) => identifier.trim().toLowerCase())
      .filter(Boolean)
  );
}

/**
 * Performs the first string operation.
 *
 * @param {Array<*>} values - Values to inspect or transform.
 * @returns {*} The derived operation result.
 */
function firstString(...values) {
  return values.flat().find((value) => typeof value === 'string' && value.trim()) || '';
}

/**
 * Retrieves account email.
 *
 * @param {number} account - Authenticated account used for this operation.
 * @returns {*} The derived operation result.
 */
function getAccountEmail(account) {
  const claims = account?.idTokenClaims || {};
  return firstString(
    claims.email,
    claims.emails,
    claims.preferred_username,
    claims.upn,
    claims.unique_name,
    claims.signInNames?.emailAddress,
    claims['signInNames.emailAddress'],
    claims.login_hint,
    account?.username
  );
}

/**
 * Retrieves account identifiers.
 *
 * @param {number} account - Authenticated account used for this operation.
 * @returns {*} The derived operation result.
 */
function getAccountIdentifiers(account) {
  const claims = account?.idTokenClaims || {};
  return new Set(
    [
      getAccountEmail(account),
      claims.oid,
      claims.sub,
      claims.tfp,
      claims.acr,
      account?.localAccountId,
      account?.homeAccountId,
      account?.tenantId,
    ]
      .filter(Boolean)
      .map((value) => String(value).toLowerCase())
  );
}

/**
 * Determines whether allowed account.
 *
 * @param {number} account - Authenticated account used for this operation.
 * @returns {boolean} Whether the requested condition is satisfied.
 */
function isAllowedAccount(account) {
  const allowedIdentifiers = getAllowedIdentifiers();
  if (allowedIdentifiers.size === 0) {
    return process.env.NODE_ENV !== 'production';
  }
  const accountIdentifiers = getAccountIdentifiers(account);
  return [...allowedIdentifiers].some((allowedIdentifier) => accountIdentifiers.has(allowedIdentifier));
}

/**
 * Renders the access pending view experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.user - Authenticated user context.
 * @param {*} props.theme - Active application theme.
 * @param {*} props.onSignOut - Callback invoked when sign out occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function AccessPendingView({ user, theme, onSignOut }) {
  const email = getAccountEmail(user);
  const strings = commonStrings.auth.accessPending;

  return (
    <div className="app-container" data-theme={theme}>
      <main className="access-pending-page">
        <section className="access-pending-card">
          <p className="eyebrow">{strings.eyebrow}</p>
          <h1>{strings.title}</h1>
          <p>
            {strings.detail}
            {email ? strings.signedInAs(email) : strings.emailUnavailable}
          </p>
          <div className="today-actions">
            <button type="button" className="btn-primary" onClick={onSignOut}>{commonStrings.actions.signOut}</button>
          </div>
        </section>
      </main>
    </div>
  );
}

/**
 * Renders the session expired view experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.theme - Active application theme.
 * @param {*} props.onSignIn - Callback invoked when sign in occurs.
 * @param {*} props.onSignOut - Callback invoked when sign out occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function SessionExpiredView({ theme, onSignIn, onSignOut }) {
  const strings = commonStrings.auth.sessionExpired;

  return (
    <div className="app-container" data-theme={theme}>
      <main className="access-pending-page">
        <section className="access-pending-card">
          <p className="eyebrow">{strings.eyebrow}</p>
          <h1>{strings.title}</h1>
          <p>{strings.detail}</p>
          <div className="today-actions">
            <button type="button" className="btn-primary" onClick={onSignIn}>{commonStrings.actions.signIn}</button>
            <button type="button" className="btn-secondary" onClick={onSignOut}>{commonStrings.actions.signOut}</button>
          </div>
        </section>
      </main>
    </div>
  );
}

/**
 * Performs the profile bootstrap operation.
 *
 * @returns {*} The derived operation result.
 */
function ProfileBootstrap() {
  useQuery(GET_MY_PROFILE);
  return null;
}

/**
 * Renders the app content experience.
 *
 * @returns {JSX.Element} The rendered interface.
 */
function AppContent() {
  const { inProgress, instance, accounts } = useMsal();
  const user = instance.getActiveAccount() || accounts[0] || null;
  const userCacheKey = user ? apolloCacheKeyForAccount(user) : null;
  const [themePreference, setThemePreference] = useState(() => localStorage.getItem('hobtalk-theme') || 'dark');
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isApolloCacheReady, setIsApolloCacheReady] = useState(false);
  const [systemTheme, setSystemTheme] = useState(() => (
    window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark'
  ));
  const theme = themePreference === 'system' ? systemTheme : themePreference;
  const apolloCache = useMemo(() => createApolloCache(), [userCacheKey]);

  useEffect(() => {
    /**
     * Retrieves access token.
     *
     * @returns {Promise<*>} A promise resolving to the completed operation result.
     */
    const getAccessToken = async () => {
      const account = instance.getActiveAccount() || user;
      if (!account) return null;
      const response = await instance.acquireTokenSilent({ ...apiTokenRequest, account });
      return response.accessToken;
    };
    configureTelemetry({
      endpoint: process.env.REACT_APP_GRAPHQL_URI || 'http://localhost:7071/api/graphql',
      getAccessToken,
      enabled: process.env.NODE_ENV === 'production' || process.env.REACT_APP_TELEMETRY_ENABLED === 'true',
    });
  }, [instance, user]);

  useEffect(() => {
    /**
     * Handles error.
     *
     * @param {*} event - Browser or React event that triggered the action.
     * @returns {void} No value; the operation applies its documented side effects.
     */
    const handleError = (event) => {
      trackClientError('client.error', event.error || event.message, { source: 'window.onerror' });
    };
    /**
     * Handles unhandled rejection.
     *
     * @param {*} event - Browser or React event that triggered the action.
     * @returns {void} No value; the operation applies its documented side effects.
     */
    const handleUnhandledRejection = (event) => {
      trackClientError('client.error', event.reason, { source: 'unhandledrejection' });
    };
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  useEffect(() => {
    if (user) {
      trackTelemetry('auth.signin.succeeded', { accountType: 'msal' });
    }
  }, [user]);

  useEffect(() => {
    localStorage.setItem('hobtalk-theme', themePreference);
  }, [themePreference]);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.('(prefers-color-scheme: light)');
    if (!mediaQuery) return undefined;
    /**
     * Updates system theme.
     *
     * @returns {void} No value; the operation applies its documented side effects.
     */
    const updateSystemTheme = () => setSystemTheme(mediaQuery.matches ? 'light' : 'dark');
    updateSystemTheme();
    mediaQuery.addEventListener?.('change', updateSystemTheme);
    return () => mediaQuery.removeEventListener?.('change', updateSystemTheme);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setIsApolloCacheReady(false);
    if (!userCacheKey) {
      setIsApolloCacheReady(true);
      return () => {
        cancelled = true;
      };
    }
    restoreApolloCache(apolloCache, userCacheKey).finally(() => {
      if (!cancelled) {
        setIsApolloCacheReady(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [apolloCache, userCacheKey]);

  const client = useMemo(() => {
    const authLink = setContext(async (_, { headers }) => {
      const account = instance.getActiveAccount() || user;
      if (!account) {
        setSessionExpired(true);
        throw new Error(commonStrings.auth.signInAgain);
      }
      if (account && !instance.getActiveAccount()) {
        instance.setActiveAccount(account);
      }
      let response;
      try {
        response = await instance.acquireTokenSilent({
          ...apiTokenRequest,
          account
        });
      } catch (error) {
        if (isAuthFailure(error)) {
          setSessionExpired(true);
          throw new Error(commonStrings.auth.signInAgain);
        }
        throw error;
      }

      return {
        headers: {
          ...headers,
          authorization: `Bearer ${response.accessToken}`,
          ...(response.idToken ? { 'x-hobtalk-id-token': response.idToken } : {}),
        }
      };
    });

    const authErrorLink = onError(({ graphQLErrors, networkError, operation }) => {
      const hasAuthError = Boolean(
        graphQLErrors?.some(isAuthFailure) ||
        isAuthFailure(networkError)
      );
      graphQLErrors?.forEach((error) => {
        trackClientError('client.graphql.failed', error, {
          operationName: operation.operationName,
          code: error.extensions?.code,
          correlationId: error.extensions?.correlationId,
          rateLimitSource: error.extensions?.rateLimitSource,
          rateLimitScope: error.extensions?.rateLimitScope,
          rateLimitProvider: error.extensions?.rateLimitProvider,
        });
      });
      if (networkError) {
        trackClientError('client.graphql.failed', networkError, {
          operationName: operation.operationName,
          network: true,
        });
      }
      if (hasAuthError) {
        trackTelemetry('auth.signin.failed', { reason: 'session_expired' }, { severity: 'warning' });
        setSessionExpired(true);
      }
    });

    const homeInvalidationLink = new ApolloLink((operation, forward) => (
      forward(operation).map((result) => {
        if (shouldInvalidateHome(operation.operationName, result)) {
          invalidateHomeQueries(apolloCache);
        }
        return result;
      })
    ));

    const persistLink = new ApolloLink((operation, forward) => (
      forward(operation).map((result) => {
        scheduleApolloCachePersist(apolloCache, userCacheKey);
        return result;
      })
    ));

    return new ApolloClient({
      link: authErrorLink.concat(authLink).concat(homeInvalidationLink).concat(persistLink).concat(httpLink),
      cache: apolloCache
    });
  }, [apolloCache, instance, user, userCacheKey]);

  /**
   * Handles sign in again.
   *
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const handleSignInAgain = () => {
    setSessionExpired(false);
    instance.loginRedirect({
      ...loginRequest,
      redirectStartPage: window.location.href,
    });
  };

  /**
   * Handles sign out.
   *
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSignOut = async () => {
    setSessionExpired(false);
    await client.clearStore().catch(() => {});
    await clearPersistedApolloCache(userCacheKey);
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  // If MSAL is still logging you in, don't show a blank screen
  if (inProgress !== "none") {
    return <HobtalkLoader variant="page" label={commonStrings.auth.authenticating} />;
  }

  if (user && !isApolloCacheReady) {
    return <HobtalkLoader variant="page" label={commonStrings.auth.opening} />;
  }

  if (sessionExpired) {
    return (
      <SessionExpiredView
        theme={theme}
        onSignIn={handleSignInAgain}
        onSignOut={handleSignOut}
      />
    );
  }

  if (!isAllowedAccount(user)) {
    return (
      <AccessPendingView
        user={user}
        theme={theme}
        onSignOut={handleSignOut}
      />
    );
  }

  return (
    <ApolloProvider client={client}>
      <ProfileBootstrap />
      <Router>
      <div className="app-container" data-theme={theme}>
        <Navbar />
        <ShareHighlightHost />
        <main className="content">
          <Routes>
            <Route path="/" element={<HomeDashboard />} />
            <Route path="/hobbies" element={<Navigate to="/hobby/cooking" replace />} />
            <Route path="/insights" element={<InsightsView />} />
            <Route path="/hobby/:hobbyId/*" element={<HobbyDetailView />} />
            <Route path="/hobbies/*" element={<Navigate to="/hobby/cooking" replace />} />
            <Route path="/account" element={<AccountView user={user} theme={themePreference} onThemeChange={setThemePreference} />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </main>
      </div>
    </Router>
    </ApolloProvider>
  );
}

/**
 * Renders the app experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.msalInstance - msal instance value.
 * @returns {JSX.Element} The rendered interface.
 */
export default function App({ msalInstance }) {
  return (
    <MsalProvider instance={msalInstance}>
      <AuthenticatedTemplate>
        <AppContent />
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <LandingView />
      </UnauthenticatedTemplate>
    </MsalProvider>
  );
}
